import { useDialog } from 'components';
import {
  Dispatch,
  FC,
  PropsWithChildren,
  RefObject,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

export type TireDamageType =
  | 'CHANNEL CRACK'
  | 'CUT TO CORD'
  | 'CUT - MINOR'
  | 'FLAT'
  | 'PEELED CAP'
  | 'RUN FLAT'
  | 'SLID FLAT'
  | 'WORN'
  | 'UNDER-INFLATED'
  | 'PUNCTURE/FLAT'
  | 'CURBING'
  | 'WEATHERED/DRY ROT'
  | 'BLISTER'
  | 'NOT TO TIR REQUIREMENTS'
  | 'MISMATCHED';

export enum TireDamage {
  CHANNEL_CRACK = 'CHANNEL CRACK',
  CUT_TO_CORD = 'CUT TO CORD',
  CUT_MINOR = 'CUT - MINOR',
  FLAT = 'FLAT',
  PEELED_CAP = 'PEELED CAP',
  RUN_FLAT = 'RUN FLAT',
  SLID_FLAT = 'SLID FLAT',
  WORN = 'WORN',
  UNDER_INFLATED = 'UNDER-INFLATED',
  PUNCTURE_FLAT = 'PUNCTURE/FLAT',
  CURBING = 'CURBING',
  WEATHERED_DRY_ROT = 'WEATHERED/DRY ROT',
  BLISTER = 'BLISTER',
  NT = 'NOT TO TIR REQUIREMENTS',
  MM = 'MISMATCHED'
}

export type TireInspection = {
  transactionId?: number;
  damageType?: TireDamageType;
  measurement0?: number;
  measurement1?: number;
  measurement2?: number;
  notes?: string;
  axle: 'FRONT' | 'CENTER' | 'REAR';
  centrality: 'INNER' | 'OUTER';
  side: 'LEFT' | 'RIGHT';
  imageKey?: string;
  imageBase64?: string;
};

export const tireDamageKeys: (keyof TireInspections)[] = [
  'frontLeftOutside',
  'frontLeftInside',
  'centerLeftOutside',
  'centerLeftInside',
  'rearLeftOutside',
  'rearLeftInside',
  'rearRightOutside',
  'rearRightInside',
  'centerRightOutside',
  'centerRightInside',
  'frontRightOutside',
  'frontRightInside'
];

export class TireInspections {
  frontLeftOutside: TireInspection = { axle: 'FRONT', side: 'LEFT', centrality: 'OUTER' };
  frontLeftInside: TireInspection = { axle: 'FRONT', side: 'LEFT', centrality: 'INNER' };
  centerLeftOutside: TireInspection = { axle: 'CENTER', side: 'LEFT', centrality: 'OUTER' };
  centerLeftInside: TireInspection = { axle: 'CENTER', side: 'LEFT', centrality: 'INNER' };
  rearLeftOutside: TireInspection = { axle: 'REAR', side: 'LEFT', centrality: 'OUTER' };
  rearLeftInside: TireInspection = { axle: 'REAR', side: 'LEFT', centrality: 'INNER' };
  rearRightOutside: TireInspection = { axle: 'REAR', side: 'RIGHT', centrality: 'OUTER' };
  rearRightInside: TireInspection = { axle: 'REAR', side: 'RIGHT', centrality: 'INNER' };
  centerRightOutside: TireInspection = { axle: 'CENTER', side: 'RIGHT', centrality: 'OUTER' };
  centerRightInside: TireInspection = { axle: 'CENTER', side: 'RIGHT', centrality: 'INNER' };
  frontRightOutside: TireInspection = { axle: 'FRONT', side: 'RIGHT', centrality: 'OUTER' };
  frontRightInside: TireInspection = { axle: 'FRONT', side: 'RIGHT', centrality: 'INNER' };
}

export const fromTireInspectionList = (inspections: TireInspection[]) => {
  const out = new TireInspections();
  out.frontLeftOutside =
    inspections.find(i => i.axle === 'FRONT' && i.side === 'LEFT' && i.centrality === 'OUTER') || out.frontLeftOutside;
  out.frontLeftInside =
    inspections.find(i => i.axle === 'FRONT' && i.side === 'LEFT' && i.centrality === 'INNER') || out.frontLeftInside;
  out.centerLeftOutside =
    inspections.find(i => i.axle === 'CENTER' && i.side === 'LEFT' && i.centrality === 'OUTER') ||
    out.centerLeftOutside;
  out.centerLeftInside =
    inspections.find(i => i.axle === 'CENTER' && i.side === 'LEFT' && i.centrality === 'INNER') || out.centerLeftInside;
  out.rearLeftOutside =
    inspections.find(i => i.axle === 'REAR' && i.side === 'LEFT' && i.centrality === 'OUTER') || out.rearLeftOutside;
  out.rearLeftInside =
    inspections.find(i => i.axle === 'REAR' && i.side === 'LEFT' && i.centrality === 'INNER') || out.rearLeftInside;
  out.rearRightOutside =
    inspections.find(i => i.axle === 'REAR' && i.side === 'RIGHT' && i.centrality === 'OUTER') || out.rearRightOutside;
  out.rearRightInside =
    inspections.find(i => i.axle === 'REAR' && i.side === 'RIGHT' && i.centrality === 'INNER') || out.rearRightInside;
  out.centerRightOutside =
    inspections.find(i => i.axle === 'CENTER' && i.side === 'RIGHT' && i.centrality === 'INNER') ||
    out.centerRightOutside;
  out.centerRightInside =
    inspections.find(i => i.axle === 'CENTER' && i.side === 'RIGHT' && i.centrality === 'OUTER') ||
    out.centerRightInside;
  out.frontRightOutside =
    inspections.find(i => i.axle === 'FRONT' && i.side === 'RIGHT' && i.centrality === 'INNER') ||
    out.frontRightOutside;
  out.frontRightInside =
    inspections.find(i => i.axle === 'FRONT' && i.side === 'RIGHT' && i.centrality === 'OUTER') || out.frontRightInside;
  return out;
};

type TireContextType = {
  capture?: boolean;
  setCapture: Dispatch<SetStateAction<boolean | undefined>>;

  currentTire?: TireInspection;
  setCurrentTire: Dispatch<SetStateAction<TireInspection>>;

  dialogRef: RefObject<HTMLDialogElement>;
  closeDialog: () => void;

  tireKey?: keyof TireInspections;
  nextTireKey?: keyof TireInspections;
  prevTireKey?: keyof TireInspections;
  selectTire: Dispatch<SetStateAction<keyof TireInspections | undefined>>;

  tires: TireInspections;
  resetTires: () => void;
  restoreTires: Dispatch<SetStateAction<TireInspections>>;

  triaxle?: boolean;
  setTriaxle: Dispatch<SetStateAction<boolean | undefined>>;
};

const TireContext = createContext<TireContextType>({
  dialogRef: { current: null },
  tires: new TireInspections(),
  closeDialog: () => {},
  resetTires: () => {},
  restoreTires: () => {},
  setCurrentTire: () => {},
  selectTire: () => {},
  setCapture: () => {},
  setTriaxle: () => {}
});

export const useTireInspections = () => useContext(TireContext);

export const TireInspectionsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { closeDialog, openDialog, ref } = useDialog();

  const [tires, setTires] = useState<TireInspections>(new TireInspections());
  const [tireKey, setTireKey] = useState<keyof TireInspections>();
  const [capture, setCapture] = useState<boolean>();
  const [triaxle, setTriaxle] = useState<boolean>();

  const tireIndex: number = useMemo(() => (!tireKey ? -1 : tireDamageKeys.indexOf(tireKey)), [tireKey]);

  const nextTireKey: keyof TireInspections | undefined = useMemo(() => {
    return tireDamageKeys.slice(tireIndex + 1).find(key => !!triaxle || !key.includes('center'));
  }, [tireIndex, triaxle]);

  const prevTireKey: keyof TireInspections | undefined = useMemo(() => {
    return tireDamageKeys
      .slice(0, tireIndex)
      .reverse()
      .find(key => !!triaxle || !key.includes('center'));
  }, [tireIndex, triaxle]);

  const currentTire: TireInspection | undefined = useMemo(() => tireKey && { ...tires[tireKey] }, [tireKey, tires]);

  /* prettier-ignore */
  const setCurrentTire = useCallback((args: SetStateAction<TireInspection>) => {
    if (!tireKey) return;
    setTires(prevState => ({ ...prevState, [tireKey]: typeof args === 'function' ? args(prevState[tireKey]) : args }));
  }, [tireKey, setTires]);

  /* prettier-ignore */
  const selectTire = useCallback((args: SetStateAction<keyof TireInspections | undefined>) => {
    if (!args) {
      closeDialog();
      setCapture(false);
      setTireKey(undefined);
    } else {
      openDialog();
      setTireKey(args);
    }
  }, [closeDialog, openDialog, setCapture, setTireKey]);

  const resetTires = useCallback(() => {
    setTires(new TireInspections());
    selectTire(undefined);
    setCapture(undefined);
    setTriaxle(undefined);
  }, [selectTire, setTires, setTriaxle]);

  return (
    <TireContext.Provider
      value={{
        capture,
        setCapture,
        currentTire,
        setCurrentTire,
        dialogRef: ref,
        closeDialog,
        tireKey,
        nextTireKey,
        prevTireKey,
        selectTire,
        tires,
        resetTires,
        restoreTires: setTires,
        triaxle,
        setTriaxle
      }}
    >
      {children}
    </TireContext.Provider>
  );
};
